import { AuditStatus } from '@arhs-fleetback/types/entities/audits';
import { omit } from 'ramda';
import { createSelector, createSelectorCreator, lruMemoize } from 'reselect';

import { AUDIT_MODEL, AUDIT_STATUS, STATUS, START_DATE_FROM, START_DATE_TO, SCHEDULABLE_TYPE, SORT_DIRECTION } from 'constants/FilterConstants';
import { DATE_TYPE } from 'constants/InterventionConstants';
import { filterRequestFormatter } from 'modules/interventions/utils';
import type { RootState } from 'reducers';
import { getSelectedDealerId } from 'selectors/dealerSelectors';

import { getByIds } from './global';

type StartDateRange = {
  [START_DATE_FROM]?: string;
  [START_DATE_TO]?: string;
};

const getStartDateRange = (
  schedulableType: string | undefined,
  now: string,
  startDateFrom?: string | null,
  startDateTo?: string | null
): StartDateRange => {
  if (schedulableType === DATE_TYPE.CURRENT) {
    return { [START_DATE_TO]: now };
  }

  if (schedulableType === DATE_TYPE.PLANNED) {
    return { [START_DATE_FROM]: now };
  }

  if (schedulableType === DATE_TYPE.SPECIFIC) {
    return {
      ...(startDateFrom ? { [START_DATE_FROM]: startDateFrom } : {}),
      ...(startDateTo ? { [START_DATE_TO]: startDateTo } : {}),
    };
  }

  return {};
};

const getRoot = (state: RootState) => state;

const getState = createSelector(getRoot, state => state.audit.ui.dashboard);

const getFilterField = (_: unknown, field: string) => field;

const getSelectedIds = createSelector(getState, state => state.ids);

export const getFilters = createSelector(getState, ({ filters }) => filters);

export const getPersistedFilters = createSelector(getFilters, filters => omit([AUDIT_MODEL], filters));

export const getFilter = createSelector(getFilters, getFilterField, (filters, field) =>
  (filters as Record<string, unknown> | undefined)?.[field]);

export const getAudits = createSelector(getRoot, getSelectedIds, getByIds);

export const hasNext = createSelector(getState, ({ hasNext: hasNextValue }) => hasNextValue);

export const getPage = createSelector(getState, ({ page }) => page);

export const getSchedulableStartDateRange = createSelector(getFilters, filters =>
  getStartDateRange(
    filters?.[SCHEDULABLE_TYPE],
    new Date().toISOString(),
    filters?.[START_DATE_FROM],
    filters?.[START_DATE_TO]
  ));

const getNow = (_: unknown, now?: string) => now ?? new Date().toISOString();
const customSelector = createSelectorCreator(lruMemoize);
export const getBaseRequestParams = customSelector(
  getFilters,
  getPage,
  getSelectedDealerId,
  getNow,
  (filters, page, dealerId, now) => {
    const {
      [STATUS]: auditStatus,
      archived: _archived,
      [SCHEDULABLE_TYPE]: schedulableType,
      [SORT_DIRECTION]: sortDirection,
      [START_DATE_FROM]: startDateFrom,
      [START_DATE_TO]: startDateTo,
      ...formattedFilters
    } = filterRequestFormatter(filters) as {
      [STATUS]?: AuditStatus[];
      archived?: boolean;
      [SCHEDULABLE_TYPE]?: string;
      [SORT_DIRECTION]?: string;
      [START_DATE_FROM]?: string | null;
      [START_DATE_TO]?: string | null;
      [key: string]: unknown;
    };

    const defaultAuditStatus = filters?.archived
      ? [AuditStatus.DRAFT, AuditStatus.OPEN, AuditStatus.TO_START, AuditStatus.CLOSED]
      : [AuditStatus.DRAFT, AuditStatus.OPEN, AuditStatus.TO_START];

    return {
      page,
      dealerId,
      size: 20,
      ...getStartDateRange(schedulableType, now, startDateFrom, startDateTo),
      [AUDIT_STATUS]: auditStatus ?? defaultAuditStatus,
      sort: `startDate,${sortDirection ?? 'desc'}`,
      ...formattedFilters,
    };
  }
);

export const isCreationModalOpen = createSelector(getState, state => state.isCreationModalOpen);
